/**
 * <PaisaMark> for React DOM (web, admin). Geometry and wordmark come from this package, so a logo change is made in
 * packages/brand only. The phone draws the same data with react-native-svg (apps/mobile BarsMark / Wordmark).
 */
import { MARK } from "./mark"
import { LOCKUP, STACKED, WORDMARK_D, WORDMARK_SOFT, WORDMARK_VIEWBOX } from "./wordmark"

type PaisaMarkVariant = "icon" | "horizontal" | "stacked" | "wordmark"
export type PaisaMarkTone = "accent" | "mono-ink" | "mono-white" | "reversed"

interface PaisaMarkProps {
  /** Layout: bare mark, mark + wordmark side by side, mark above wordmark, or text only. */
  variant?: PaisaMarkVariant
  /**
   * Colour treatment. "accent" tracks the live Fern accent token (theme-aware); the mono/reversed tones are fixed
   * colours for backgrounds the theme can't predict. "reversed" is for an accent-filled tile.
   */
  tone?: PaisaMarkTone
  /** Height of the mark in px (wordmark-only: the size the word replaces, like a font size). */
  size?: number
  className?: string
  /** Accessible label, defaults to "Paisa". Ignored when `decorative` is set. */
  "aria-label"?: string
  /** aria-hidden instead of labelled: use when adjacent text already announces "Paisa". */
  decorative?: boolean
}

const TONE_COLOR: Record<PaisaMarkTone, string> = {
  accent: "var(--accent-primary)",
  "mono-ink": "#171b21",
  "mono-white": "#ffffff",
  reversed: "#ffffff",
}
// In "accent" only the mark carries the brand hue; the word reads as ordinary ink (theme-aware).
const wordColor = (tone: PaisaMarkTone) =>
  tone === "accent" ? "var(--text-primary)" : TONE_COLOR[tone]

function MarkShapes({ color }: { color: string }) {
  return (
    <g fill={color} style={{ color }}>
      {MARK.bars.map((b) => (
        <rect key={b.y} x={b.x} y={b.y} width={b.width} height={b.height} rx={b.rx} />
      ))}
      <path
        d={MARK.leg}
        fill="none"
        stroke="currentColor"
        strokeWidth={MARK.stroke}
        strokeLinecap="round"
      />
    </g>
  )
}
function Word({ color, transform }: { color: string; transform?: string }) {
  return (
    <path
      transform={transform}
      d={WORDMARK_D}
      fill={color}
      stroke={color}
      strokeWidth={WORDMARK_SOFT}
      strokeLinejoin="round"
    />
  )
}

export function PaisaMark({
  variant = "icon",
  tone = "accent",
  size = 24,
  className,
  decorative = false,
  ...rest
}: PaisaMarkProps) {
  const markColor = TONE_COLOR[tone]
  const textColor = wordColor(tone)
  const a11y = decorative
    ? { "aria-hidden": true as const }
    : { role: "img" as const, "aria-label": rest["aria-label"] ?? "Paisa" }
  const box = MARK.box
  let svg: React.ReactNode
  if (variant === "wordmark") {
    const [, , w, h] = WORDMARK_VIEWBOX.split(" ").map(Number)
    svg = (
      <svg
        viewBox={WORDMARK_VIEWBOX}
        width={(size * w) / 1000}
        height={(size * h) / 1000}
        aria-hidden="true"
      >
        <Word color={textColor} />
      </svg>
    )
  } else if (variant === "horizontal") {
    svg = (
      <svg
        viewBox={`0 0 ${LOCKUP.width} ${box}`}
        width={(size * LOCKUP.width) / box}
        height={size}
        aria-hidden="true"
      >
        <MarkShapes color={markColor} />
        <Word
          color={textColor}
          transform={`translate(${LOCKUP.x} ${LOCKUP.y}) scale(${LOCKUP.scale})`}
        />
      </svg>
    )
  } else if (variant === "stacked") {
    svg = (
      <svg
        viewBox={`0 0 ${STACKED.width} ${STACKED.height}`}
        width={(size * STACKED.width) / box}
        height={(size * STACKED.height) / box}
        aria-hidden="true"
      >
        <g transform={`translate(${STACKED.markX} 0)`}>
          <MarkShapes color={markColor} />
        </g>
        <Word
          color={textColor}
          transform={`translate(${STACKED.wordX} ${STACKED.wordY}) scale(${STACKED.scale})`}
        />
      </svg>
    )
  } else {
    svg = (
      <svg viewBox={`0 0 ${box} ${box}`} width={size} height={size} aria-hidden="true">
        <MarkShapes color={markColor} />
      </svg>
    )
  }
  return (
    <span className={className} {...a11y} style={{ display: "inline-flex", flexShrink: 0 }}>
      {svg}
    </span>
  )
}
